import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Icon } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnGroup, Column } from '@cards/Card';
import { useHasPermissions } from '@authorisation/hooks';
import { usePageActions, useTitleUpdate, useFeature } from '@hooks';
import LinkCard from '@cards/Link';
import { addDocument, addMessage, addProcedure, addAppUser } from '@assets/icons';
import HospitalAdminOnboarding from '@cards/Hospitals/AdminOnboarding';
import { Permissions } from '@authorisation/constants';
import HeaderButton from '@components/HeaderButton';
import { fetchRules } from '@pathways/redux/rules/actions';

import { screens, setCurrentScreen } from '../../analytics';

const HelpButton = () => {
  const { t } = useTranslation();
  const { help } = useFeature('help');
  return help && help.url ? (
    <HeaderButton
      href={help.url}
      key="new-patient-button"
      rel="noopener noreferrer"
      target="_blank"
      tooltipPlacement="bottomRight"
      tooltip={t('home:helpButton.tooltip')}
    >
      {t('home:helpButton.label')}
    </HeaderButton>
  ) : null;
};

export default () => {
  const { t } = useTranslation();
  const history = useHistory();
  const perms = useHasPermissions();
  const dispatch = useDispatch();
  const shouldFetchRules = useSelector(
    state => state.rules.data.length === 0 && !state.rules.loading,
  );
  const { setTitle } = usePageActions({
    title: t('home:header'),
    actions: [<HelpButton />],
  });
  useTitleUpdate(setTitle, 'home:header');
  document.title = t('home:header');

  useEffect(() => {
    setCurrentScreen(screens.HOME_HOME);
    if (shouldFetchRules) {
      dispatch(fetchRules());
    }
  }, [dispatch, shouldFetchRules]);

  return (
    <ColumnGroup>
      <Column>
        {perms[Permissions.AdministerHospital] ? <HospitalAdminOnboarding /> : null}
        {perms[Permissions.ViewPatients] ? (
          <LinkCard
            title={t('home:links.patients.title')}
            description={t('home:links.patients.description')}
            onContentClick={() => history.push('/patients/individuals')}
            showButton={Boolean(perms[Permissions.ManagePatients])}
            buttonIcon={<Icon component={addAppUser} />}
            buttonLabel={t('home:links.patients.buttonLabel')}
            buttonClick={() => history.push('/patients/individuals/new')}
          />
        ) : null}
        <LinkCard
          title={t('home:links.content.title')}
          description={t('home:links.content.description')}
          onContentClick={() => history.push('/content/documents')}
          buttonIcon={<Icon component={addDocument} />}
          buttonLabel={t('home:links.content.buttonLabel')}
          buttonClick={() => history.push('/content/documents/new')}
        />
      </Column>
      <Column>
        {perms[Permissions.ViewMessages] ? (
          <LinkCard
            title={t('home:links.messages.title')}
            description={t('home:links.messages.description')}
            onContentClick={() => history.push('/messages')}
            showButton={Boolean(perms[Permissions.ViewMessages])}
            buttonIcon={<Icon component={addMessage} />}
            buttonLabel={t('home:links.messages.buttonLabel')}
            buttonClick={() => history.push('/messages/new')}
          />
        ) : null}
        <LinkCard
          title={t('home:links.procedurePathways.title')}
          description={t('home:links.procedurePathways.description')}
          onContentClick={() => history.push('/procedure/pathways')}
          showButton={!!perms[Permissions.CreatePathway]}
          buttonIcon={<Icon component={addProcedure} />}
          buttonLabel={t('home:links.procedurePathways.buttonLabel')}
          buttonClick={() => history.push('/procedure/pathways/new')}
        />
      </Column>
    </ColumnGroup>
  );
};
